import React, { useEffect, useRef, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import {
  MuiBox,
  MuiContainer,
  MuiGrid,
  dfdDefaultTheme,
  MuiDivider,
  MuiTypography
} from 'theme/material-ui';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';

import DataLoader from 'components/UI/DataLoader/DataLoader';
import Spinner from 'components/UI/Spinner/Spinner';
import { InfoCard } from 'components/InfoCard';
import DashboardEmptyStateCard from 'screens/Dashboard/DashboardEmptyStateCard';

import { IconSize, FontWeight } from 'modules/styles/variables';
import { Color } from 'modules/styles/colors';

import analyticsService, { AmplitudeEventData, AnalyticsEvent } from 'services/AnalyticsService';

import * as bookingTypes from 'store/booking/types';
import * as bookingActions from 'store/booking/actions';
import * as findProviderActions from 'store/findProvider/actions';
import { mergeFiltersAndFacets } from 'store/findProvider/utils';
import * as findProviderConstants from 'store/findProvider/constants';
import { ProviderSearchFilters, ProviderSummary } from 'store/findProvider/types';
import { getMyDoctors } from 'shared/src/store/myDoctors/actions';

import { useCheckBookingInfoEffect } from '../useCheckBookingInfoEffect';
import { StyledScreen } from '../components/styled';
import { BookingLoading, BookingNoData } from '../components/sharedComponents';
import SpecialtyOrMyDoctorSelectMoreInfoDialog from '../dialogs/SpecialtyOrMyDoctorSelectMoreInfoDialog';
import { convertToLowerKabobCase } from 'modules/utils/StringUtils';
import SpecialtiesSection from './specialtiesSection';
import ProvidersSection from './providersSection';

export interface Props extends RouteComponentProps {
  appointmentDetails: bookingTypes.AppointmentDetails;
  doctorsLoading: boolean;
  doctors: ProviderSummary[];
  geolocation: Coordinates;
  geolocationStatus: boolean;
  getSpecialties: typeof bookingActions.getSpecialties;
  getMyDoctors: typeof getMyDoctors;
  setActiveProviderSearchFilter: typeof findProviderActions.setActiveProviderSearchFilter;
  setDoctor: typeof bookingActions.setDoctor;
  setSpecialty: typeof bookingActions.setSpecialty;
  specialties: bookingTypes.Specialty[];
  specialtiesLoading: boolean;
  setSort: typeof findProviderActions.setSortBy;
  providerSearch: typeof findProviderActions.schedulingProviderSearch;
  currentRouteName?: string;
  previousRouteName?: string;
}

export function BookingSpecialtyOrMyDoctorSelectNPV({
  appointmentDetails,
  doctors,
  doctorsLoading,
  geolocationStatus,
  getMyDoctors,
  getSpecialties,
  setActiveProviderSearchFilter,
  setDoctor,
  setSpecialty,
  specialties,
  specialtiesLoading,
  setSort,
  providerSearch,
  history,
  currentRouteName,
  previousRouteName
}: Props) {
  const eventData: AmplitudeEventData = {
    currentUrl: currentRouteName,
    referringUrl: previousRouteName
  };

  const persistRef = useRef(false);

  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);
  // const [selectedSpecialty, setSelectedSpecialty] = useState<SpecialtyStub | null>(null);
  const [selectedSpecialty, setSelectedSpecialty] = useState<bookingTypes.Specialty | null>(null);
  const isSmallScreen = useMediaQuery(dfdDefaultTheme.breakpoints.down('md'));
  const isLoading = specialtiesLoading || doctorsLoading;

  useEffect(() => {
    getSpecialties();
    getMyDoctors();
  }, []);

  useCheckBookingInfoEffect(appointmentDetails?.patient);

  const handleMoreInfoOpen = () => {
    const helpUsedData: AmplitudeEventData = {
      ...eventData,
      /* eslint-disable-next-line @typescript-eslint/camelcase */
      source_page: 'SelectProviderPage'
    };
    analyticsService.logEvent(AnalyticsEvent.BookingHelpUsed, helpUsedData);
    setIsMoreInfoDialogOpen(true);
  };

  const handleSpecialtySelect = (
    event: React.ChangeEvent<{ value: bookingTypes.Specialty | null }>
  ) => {
    setSpecialty(event.target.value);
    setSelectedSpecialty(event.target.value);

    const filters = { isAcceptingOnlineBooking: true, hasOnlineNpvSlots: true };

    const facets = {
      specialty: [
        {
          name: event.target.value?.nameLong || '',
          group: 'specialty',
          value: event.target.value?.nameLong || ''
        }
      ]
    };

    const dispatchSearchFilters: ProviderSearchFilters = mergeFiltersAndFacets(filters, facets);

    // sets filters and facets
    setActiveProviderSearchFilter(dispatchSearchFilters);
    // sets sort order
    setSort(findProviderConstants.SORT_BY_PROXIMITY);
    // fires off search
    providerSearch();

    persistRef.current = true;

    history.push('/u/get-care-now/booking/specialty-provider-select');
  };

  const handleProviderSelect = (provider: ProviderSummary) => {
    setDoctor(provider);
    history.push('/u/get-care-now/booking/visit-type-select');

    analyticsService.logEvent(AnalyticsEvent.BookingProviderSelected, eventData);
  };

  const getProviderIsSelected = (provider: ProviderSummary) => {
    if (!provider || !appointmentDetails.doctor) {
      return false;
    }

    return appointmentDetails.doctor.corpProvId === provider.corpProvId;
  };

  const NoCareTeam = (
    <>
      <MuiBox mb={2}>
        <MuiTypography gutterBottom variant="h5">
          My Care Team
        </MuiTypography>
        <MuiDivider />
      </MuiBox>
      <InfoCard testID="no-data-mycare-team-list">
        <DashboardEmptyStateCard
          iconName="MyCareTeam"
          title="My Care Team"
          iconBgColor={Color.primary}
          subtitle={
            <MuiTypography>
              You don't have anyone schedulable in your care team. If you would like to find a new
              provider, navigate to{' '}
              <i style={{ color: Color.primary, fontWeight: FontWeight.semibold }}>New Provider</i>{' '}
              section.
            </MuiTypography>
          }
        />
      </InfoCard>
    </>
  );

  return (
    <>
      <StyledScreen>
        <MuiBox mt={3} mx="auto" maxWidth={850}>
          <MuiContainer>
            <MuiGrid
              container
              spacing={5}
              alignItems="flex-start"
              direction={isSmallScreen ? 'column-reverse' : 'row'}
            >
              {/* Column 1 */}
              <MuiGrid item lg={6} md={7} xs={12}>
                <DataLoader
                  data={doctors}
                  loading={isLoading}
                  renderLoading={() => (
                    <MuiBox textAlign="center" paddingTop={3}>
                      <Spinner
                        size={IconSize.xLarge}
                        data-testid="booking-specialty-or-my-doctor-select-loading"
                      />
                      <BookingLoading />
                    </MuiBox>
                  )}
                  renderNoData={() => NoCareTeam}
                  renderData={() => (
                    <ProvidersSection
                      doctors={doctors}
                      doctorsLoading={doctorsLoading}
                      geolocationStatus={geolocationStatus}
                      getProviderIsSelected={getProviderIsSelected}
                      handleProviderSelect={handleProviderSelect}
                      convertToLowerKabobCase={convertToLowerKabobCase}
                    />
                  )}
                />
              </MuiGrid>
              {/* Column 2 */}
              <MuiGrid container item lg={6} md={5} xs={12}>
                <SpecialtiesSection
                  handleMoreInfoOpen={handleMoreInfoOpen}
                  selectedSpecialty={selectedSpecialty}
                  handleSpecialtySelect={handleSpecialtySelect}
                  specialties={specialties}
                />
              </MuiGrid>
            </MuiGrid>
          </MuiContainer>
        </MuiBox>
      </StyledScreen>

      <SpecialtyOrMyDoctorSelectMoreInfoDialog
        isOpen={isMoreInfoDialogOpen}
        handleClose={() => setIsMoreInfoDialogOpen(false)}
      />
    </>
  );
}

export default BookingSpecialtyOrMyDoctorSelectNPV;
